import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users, FileText, BookOpen, Award, Shield, TrendingUp, Mail, MapPin, 
  ClipboardCheck, BarChart2, UserPlus, CalendarPlus, Gauge, Video,
  Image as ImageIcon, Heart
} from 'lucide-react';
import axios from 'axios';

// Import Admin Tab Components
import UserManagementTab from '@/components/admin/UserManagementTab';
import VivaSchedulingTab from '@/components/admin/VivaSchedulingTab';
import ExaminerCalibrationTab from '@/components/admin/ExaminerCalibrationTab';
import MasterAdminTab from '@/components/admin/MasterAdminTab';
import GoogleMeetSetup from '@/components/admin/GoogleMeetSetup';
import ApplicationsTab from '@/components/admin/ApplicationsTab';
import QuestionsTab from '@/components/admin/QuestionsTab';
import CertificatesTab from '@/components/admin/CertificatesTab';
import UsersTab from '@/components/admin/UsersTab';
import ContactsTab from '@/components/admin/ContactsTab';
import ApplicationManagementTab from '@/components/admin/ApplicationManagementTab';
import AccreditationAppsTab from '@/components/admin/AccreditationAppsTab';
import ExamCenterAppsTab from '@/components/admin/ExamCenterAppsTab';
import CaseLogsTab from '@/components/admin/CaseLogsTab';
import MarkSheetsTab from '@/components/admin/MarkSheetsTab';
import ExamConfigTab from '@/components/admin/ExamConfigTab';
import ProgramRegistrationsTab from '@/components/admin/ProgramRegistrationsTab';
import EventsGalleryTab from '@/components/admin/EventsGalleryTab';
import AnalyticsTab from '@/components/admin/AnalyticsTab';
import CPDRecognitionTab from '@/components/admin/CPDRecognitionTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stat Card Component
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="imcac-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-sm ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-bold text-[#1B2A4A]">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Curriculum Apps Tab (wrapper for generic ApplicationManagementTab)
function CurriculumAppsTab({ token }) {
  return <ApplicationManagementTab token={token} endpoint="/admin/curriculum-applications" title="Curriculum" nameField="applicant_name" idField={null} />;
}

export default function AdminPage() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setStats(res.data))
        .catch(console.error);
    }
  }, [token]);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-access-denied">
        <Card className="imcac-card max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1B2A4A] font-heading mb-2">Access Denied</h2>
            <p className="text-sm text-slate-500">This page is restricted to IMCAC administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="admin-page">
      <section className="bg-[#1B2A4A] py-8">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="imcac-overline mb-1">Administration</p>
              <h1 className="text-3xl font-bold text-white font-heading">IMCAC Admin Panel</h1>
              <p className="text-sm text-slate-300 mt-1">Manage applications, MCQ questions, certificates, and users</p>
            </div>
            <Link to="/admin/analytics">
              <Button className="bg-[#C5A572] hover:bg-[#B8956A] text-[#1B2A4A] font-semibold rounded-sm" data-testid="analytics-dashboard-btn">
                <TrendingUp className="h-4 w-4 mr-2" />
                Analytics Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} color="bg-blue-100 text-blue-600" />
            <StatCard icon={FileText} label="Applications" value={`${stats.pending_applications} pending / ${stats.total_applications}`} color="bg-amber-100 text-amber-600" />
            <StatCard icon={BookOpen} label="MCQ Questions" value={stats.total_mcq_questions} color="bg-purple-100 text-purple-600" />
            <StatCard icon={Award} label="Certificates" value={stats.total_certificates} color="bg-green-100 text-green-600" />
          </div>
        )}

        {/* Tabs */}
        <Card className="imcac-card">
          <CardContent className="p-0">
            <Tabs defaultValue="applications">
              <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1" data-testid="admin-tabs">
                <TabsTrigger value="applications" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-applications">
                  <FileText className="h-3.5 w-3.5 mr-1.5" />Exam Apps
                </TabsTrigger>
                <TabsTrigger value="accreditation" className="text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white rounded-sm" data-testid="tab-accreditation">
                  <Award className="h-3.5 w-3.5 mr-1.5" />Accreditation
                </TabsTrigger>
                <TabsTrigger value="curriculum" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-curriculum">
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" />Curriculum
                </TabsTrigger>
                <TabsTrigger value="examcenters" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-examcenters">
                  <MapPin className="h-3.5 w-3.5 mr-1.5" />Exam Centers
                </TabsTrigger>
                <TabsTrigger value="caselogs" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-caselogs">
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />Case Logs
                </TabsTrigger>
                <TabsTrigger value="questions" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-questions">
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />MCQ Questions
                </TabsTrigger>
                <TabsTrigger value="certificates" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-certificates">
                  <Award className="h-3.5 w-3.5 mr-1.5" />Certificates
                </TabsTrigger>
                <TabsTrigger value="marksheets" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-transcripts">
                  <BarChart2 className="h-3.5 w-3.5 mr-1.5" />Transcripts
                </TabsTrigger>
                <TabsTrigger value="examconfig" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-examconfig">
                  <Shield className="h-3.5 w-3.5 mr-1.5" />Exam Config
                </TabsTrigger>
                <TabsTrigger value="users" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-users">
                  <Users className="h-3.5 w-3.5 mr-1.5" />Users
                </TabsTrigger>
                <TabsTrigger value="contacts" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-contacts">
                  <Mail className="h-3.5 w-3.5 mr-1.5" />Messages
                </TabsTrigger>
                <TabsTrigger value="usermanagement" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-usermanagement">
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />User Mgmt
                </TabsTrigger>
                <TabsTrigger value="vivascheduling" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-vivascheduling">
                  <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />Viva Schedule
                </TabsTrigger>
                <TabsTrigger value="calibration" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-calibration">
                  <Gauge className="h-3.5 w-3.5 mr-1.5" />Calibration
                </TabsTrigger>
                <TabsTrigger value="googlemeet" className="text-sm data-[state=active]:bg-green-600 data-[state=active]:text-white rounded-sm" data-testid="tab-googlemeet">
                  <Video className="h-3.5 w-3.5 mr-1.5" />Google Meet
                </TabsTrigger>
                <TabsTrigger value="programs" className="text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white rounded-sm" data-testid="tab-programs">
                  <Heart className="h-3.5 w-3.5 mr-1.5" />Programs
                </TabsTrigger>
                <TabsTrigger value="eventsgallery" className="text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white rounded-sm" data-testid="tab-eventsgallery">
                  <ImageIcon className="h-3.5 w-3.5 mr-1.5" />Gallery
                </TabsTrigger>
                <TabsTrigger value="analytics" className="text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="tab-analytics">
                  <BarChart2 className="h-3.5 w-3.5 mr-1.5" />Analytics
                </TabsTrigger>
                <TabsTrigger value="cpdrecognition" className="text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white rounded-sm" data-testid="tab-cpdrecognition">
                  <Award className="h-3.5 w-3.5 mr-1.5" />CPD Recognition
                </TabsTrigger>
                <TabsTrigger value="masteradmin" className="text-sm data-[state=active]:bg-[#C9A227] data-[state=active]:text-white rounded-sm" data-testid="tab-masteradmin">
                  <Shield className="h-3.5 w-3.5 mr-1.5" />Master Admin
                </TabsTrigger>
              </TabsList>

              <div className="p-5">
                <TabsContent value="applications"><ApplicationsTab token={token} /></TabsContent>
                <TabsContent value="accreditation"><AccreditationAppsTab token={token} /></TabsContent>
                <TabsContent value="curriculum"><CurriculumAppsTab token={token} /></TabsContent>
                <TabsContent value="examcenters"><ExamCenterAppsTab token={token} /></TabsContent>
                <TabsContent value="caselogs"><CaseLogsTab token={token} /></TabsContent>
                <TabsContent value="questions"><QuestionsTab token={token} /></TabsContent>
                <TabsContent value="certificates"><CertificatesTab token={token} /></TabsContent>
                <TabsContent value="marksheets"><MarkSheetsTab token={token} /></TabsContent>
                <TabsContent value="examconfig"><ExamConfigTab token={token} /></TabsContent>
                <TabsContent value="users"><UsersTab token={token} /></TabsContent>
                <TabsContent value="contacts"><ContactsTab token={token} /></TabsContent>
                <TabsContent value="usermanagement"><UserManagementTab token={token} /></TabsContent>
                <TabsContent value="vivascheduling"><VivaSchedulingTab token={token} /></TabsContent>
                <TabsContent value="calibration"><ExaminerCalibrationTab token={token} /></TabsContent>
                <TabsContent value="googlemeet"><GoogleMeetSetup /></TabsContent>
                <TabsContent value="programs"><ProgramRegistrationsTab token={token} /></TabsContent>
                <TabsContent value="eventsgallery"><EventsGalleryTab token={token} /></TabsContent>
                <TabsContent value="analytics"><AnalyticsTab token={token} /></TabsContent>
                <TabsContent value="cpdrecognition"><CPDRecognitionTab token={token} /></TabsContent>
                <TabsContent value="masteradmin"><MasterAdminTab token={token} /></TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
